import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { BaseService } from "./baseService";
import { ITherapist } from "../library/models/therapist";
import {
    ITherapistRevenueShare,
    TherapistRevenueSharePayload,
} from "../library/models/therapistRevenueShare";

/** What PUT /therapists/{id}/catalog-types takes, and who it is for. */
export interface TherapistCatalogTypesPayload {
    therapistId: number;
    catalogTypeIds: number[];
}

/** What PUT /therapists/{id}/revenue-share takes: the default rate and per-discipline overrides. */
export type { TherapistRevenueSharePayload } from "../library/models/therapistRevenueShare";

export class TherapistsService extends BaseService {
    /**
     * Who can give a service. `serviceId` is a catalogue option -- the priced line picked
     * in the wizard -- which the API resolves to the discipline behind it before answering.
     */
    getTherapists(serviceId: number): UseQueryOptions<ITherapist[], Error> {
        return this.createGetQuery<ITherapist[]>('/therapists', { serviceId });
    }

    /**
     * Every therapist at this clinic, for naming whose day an entry goes into.
     *
     * A different question from `getTherapists`, which asks who may *give* a service, and the
     * two lists deliberately differ: booking is front-desk work, the API lets staff put an
     * appointment in any colleague's diary, and somebody mapped to no discipline still has a
     * day. THERAPIST/ADMIN, and 403 for a patient — it answers identity alone, so a caller
     * cannot use it to ask about administrators or about anybody's membership.
     */
    getClinicTherapists(): UseQueryOptions<ITherapist[], Error> {
        return this.createGetQuery<ITherapist[]>('/therapists/colleagues');
    }

    /** The catalogue types this therapist offers. Admin only, and 403 for anyone else. */
    getCatalogTypes(therapistId: number): UseQueryOptions<number[], Error> {
        return this.createGetQuery<number[]>(`/therapists/${therapistId}/catalog-types`);
    }

    /**
     * Replaces the whole list: whatever is absent is removed. Hand-written rather than
     * built with createMutation, which only speaks POST/PATCH/DELETE shapes.
     */
    saveCatalogTypesMutation(): UseMutationOptions<number[], Error, TherapistCatalogTypesPayload> {
        return {
            mutationFn: (payload: TherapistCatalogTypesPayload): Promise<number[]> =>
                this.put<number[]>(`/therapists/${payload.therapistId}/catalog-types`,
                    { catalogTypeIds: payload.catalogTypeIds }),
        };
    }

    /**
     * Every therapist's revenue share, for the whole clinic. Admin only, and 403 for anyone else.
     *
     * One read so the console's therapist table renders without a call per row. One answer per
     * therapist and scope — `catalogTypeId: null` is the default, anything else a discipline's
     * override — and each carries **two** rates: the one in force, and the change queued behind
     * it — a rate set this month starts on the first of next, so the two are routinely different.
     *
     * **A therapist who has never been given a rate is absent**, and one whose share was cleared
     * comes back with a null percent. Both mean the whole income, which is why `shareFor` treats
     * a missing row and a null alike.
     */
    getRevenueShares(): UseQueryOptions<ITherapistRevenueShare[], Error> {
        return this.createGetQuery<ITherapistRevenueShare[]>('/therapists/revenue-shares');
    }

    /**
     * Sets what one therapist keeps — a default and any per-discipline overrides — or clears a
     * scope with a null percent.
     *
     * Sends no date: the server dates it the first of next month and **echoes the therapist's
     * scopes back**, which is what lets the caller say when the change starts without waiting for
     * a refetch. Hand-written for the same reason `saveCatalogTypesMutation` is — `createMutation`
     * speaks only POST/PATCH/DELETE shapes.
     */
    saveRevenueShareMutation():
        UseMutationOptions<ITherapistRevenueShare[], Error, TherapistRevenueSharePayload> {

        return {
            mutationFn: (payload: TherapistRevenueSharePayload): Promise<ITherapistRevenueShare[]> =>
                this.put<ITherapistRevenueShare[]>(
                    `/therapists/${payload.therapistId}/revenue-share`,
                    { percent: payload.percent, disciplines: payload.disciplines }),
        };
    }
}
